import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Lightbulb } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'navigation-bar', title: 'Navigation bar in the web app' },
  { id: 'dashboard', title: 'Dashboard' },
  { id: 'ai-employees', title: 'AI Employees' },
  { id: 'brain-ai', title: 'Brain AI' },
  { id: 'tasks', title: 'Tasks' },
  { id: 'analytics', title: 'Analytics' },
  { id: 'team', title: 'Team' },
  { id: 'integrations', title: 'Integrations' },
  { id: 'api', title: 'API' },
  { id: 'billing', title: 'Billing' },
  { id: 'summary', title: 'Summary' },
];

const relatedArticles = [
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'SweetPilot AI Employees', href: '/employees' },
  { title: 'Brain AI explained', href: '/doc/collections/brain-ai' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
  { title: 'Integrations explained', href: '/doc/collections/integrations' },
];

interface TipBoxProps {
  children: React.ReactNode;
  isDark: boolean;
}

function TipBox({ children, isDark }: TipBoxProps) {
  return (
    <div className={`flex gap-3 p-4 rounded-xl my-6 ${
      isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'
    }`}>
      <Lightbulb className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
      <div className={`text-sm ${isDark ? 'text-purple-200' : 'text-purple-800'}`}>
        {children}
      </div>
    </div>
  );
}

export default function NavigateWebApp() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');
  const [isAtTop, setIsAtTop] = useState(true);

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Navigate the SweetPilot Web App - Complete Guide',
    description: 'Learn how to navigate SweetPilot web app including Dashboard, AI Employees, Brain AI, Tasks, Analytics, Team management, Integrations, API, and Billing.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/navigate-web-app' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      const firstSection = sections[0];
      if (firstSection && scrollPosition < firstSection.offsetTop) {
        setIsAtTop(true);
        setActiveSection('');
        return;
      }

      setIsAtTop(false);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>How to Navigate the SweetPilot Web App - Complete Guide | SweetPilot Help Center</title>
        <meta name="description" content="Learn how to navigate SweetPilot web app. Comprehensive guide to Dashboard, AI Employees, Brain AI, Tasks, Analytics, Team management, Integrations, and Billing." />
        <meta name="keywords" content="SweetPilot navigation, SweetPilot dashboard, SweetPilot web app, AI employees interface, Brain AI guide, SweetPilot integrations, SweetPilot analytics" />
        <meta property="og:title" content="How to Navigate the SweetPilot Web App" />
        <meta property="og:description" content="Complete guide to navigating SweetPilot - Dashboard, AI Employees, Brain AI, Tasks, Analytics, and more." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/navigate-web-app" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Navigate SweetPilot Web App" />
        <meta name="twitter:description" content="Complete guide to navigating SweetPilot web app interface." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/navigate-web-app" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
        <div className="flex gap-16 lg:gap-20">
          {/* Main Content */}
          <div className="flex-1 max-w-2xl">
            {/* Breadcrumbs */}
            <nav className="mb-8">
              <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                <li>
                  <Link
                    to="/docs"
                    className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                  >
                    All Collections
                  </Link>
                </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li>
                <Link
                  to="/doc/collections/getting-started"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  Getting started
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li className={isDark ? 'text-white' : 'text-slate-900'}>
                How to navigate the SweetPilot web app
              </li>
            </ol>
          </nav>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              How to navigate the SweetPilot web app
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Learn how to navigate SweetPilot's web app, including the Dashboard, AI Employees, Brain AI, Tasks, Analytics, and more.
            </p>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
          >
            <p className={`text-lg leading-relaxed mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              This article will cover how to navigate our web app. It provides easy access to main features through the left-side menu.
            </p>

            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To access the web app, you will need to login by visiting{' '}
              <Link
                to="/login"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                sweetpilot.com/login
              </Link>{' '}
              first. Most of the navigation happens via the left side-bar. In case you have questions on SweetPilot or need help - click "Help" button or visit our{' '}
              <Link
                to="/help"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                Help Center
              </Link>.
            </p>

            {/* Navigation bar in the web app */}
            <h2 id="navigation-bar" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Navigation bar in the web app
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              The web app menu lets you access:
            </p>
            <ol className={`list-decimal pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Dashboard</strong> - Your home page with quick overview</li>
              <li><strong>AI Employees</strong> - All available AI employees</li>
              <li><strong>Brain AI</strong> - Your knowledge base</li>
              <li><strong>Tasks</strong> - Task management and tracking</li>
              <li><strong>Analytics</strong> - Usage statistics and insights</li>
              <li><strong>Team</strong> - Team member management</li>
              <li><strong>Integrations</strong> - Connect external services</li>
              <li><strong>API</strong> - API keys and documentation</li>
              <li><strong>Billing</strong> - Subscription and payment management</li>
            </ol>

            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Let's dive into each of the tabs!
            </p>

            {/* Sidebar Image */}
            <div className={`my-8 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              <img
                src="/docs/navigate/sidebar-nav.png"
                alt="SweetPilot navigation sidebar"
                className="w-full max-w-xs mx-auto block"
              />
            </div>

            {/* Dashboard */}
            <h2 id="dashboard" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Dashboard
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              The Dashboard is identified by the grid icon and is your starting point. It's your central hub featuring:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Quick access to all AI employees</li>
              <li>Recent conversations and activity</li>
              <li>Your credit balance and usage</li>
              <li>Quick action buttons for common tasks</li>
            </ul>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              From the Dashboard, you can quickly jump to any AI employee by clicking on their card, or start a new conversation right away.
            </p>

            {/* AI Employees */}
            <h2 id="ai-employees" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              AI Employees
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To access all AI Employees, click the <strong>people icon</strong> in the left panel. Here you'll find all available AI employees, each specialized for different tasks:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Browse all AI employees and their specializations</li>
              <li>Start new conversations with any employee</li>
              <li>View employee capabilities and power-ups</li>
              <li>Access your conversation history with each employee</li>
            </ul>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To interact with an AI employee, simply click on their card to open the dedicated chat interface.
            </p>
            <Link
              to="/employees"
              className={`inline-flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-colors ${
                isDark
                  ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              Learn about AI Employees
            </Link>

            {/* Brain AI */}
            <h2 id="brain-ai" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Brain AI
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To access Brain AI in the web app, click the <strong>brain icon</strong> in the left panel. Brain AI serves as your knowledge base for SweetPilot AI Employees. They will use the context of information stored in Brain AI to execute tasks.
            </p>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              In the Brain AI tab you can:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Create, edit, and delete knowledge profiles</li>
              <li>Add connections to accounts on other platforms in the "Integrations" section</li>
              <li>Add, remove and edit information (snippets of information on your brand or guidelines employees must follow, personal details, website links or even files) on your business</li>
            </ul>
            <Link
              to="/doc/collections/brain-ai"
              className={`inline-flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-colors ${
                isDark
                  ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              Learn about Brain AI
            </Link>

            {/* Tasks */}
            <h2 id="tasks" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Tasks
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              The Tasks section is accessed by clicking the <strong>lightning bolt icon</strong> in the left panel. This is where you can manage and track all your AI-generated tasks.
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>View all pending and completed tasks</li>
              <li>Track task progress and status</li>
              <li>Review AI-generated content before publishing</li>
              <li>Manage automated workflow outputs</li>
            </ul>
            <TipBox isDark={isDark}>
              <strong>Tip:</strong> Tasks that require your attention will show a notification badge. Make sure to review them regularly to keep your workflows running smoothly.
            </TipBox>

            {/* Analytics */}
            <h2 id="analytics" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Analytics
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Access Analytics by clicking the <strong>chart icon</strong> in the left panel. Here you can monitor your usage and get insights into your AI employee interactions.
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>View credit usage over time</li>
              <li>See which AI employees you use most</li>
              <li>Track conversation volumes</li>
              <li>Monitor team activity (if applicable)</li>
            </ul>

            {/* Team */}
            <h2 id="team" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Team
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              The Team section is accessed by clicking the <strong>people group icon</strong>. This is where you can manage your team members and their access.
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Invite new team members via email</li>
              <li>Manage team member roles and permissions</li>
              <li>View team activity and usage</li>
              <li>Remove team members when needed</li>
            </ul>
            <TipBox isDark={isDark}>
              <strong>Note:</strong> Team features may require a specific subscription plan. Check your{' '}
              <Link
                to="/pricing"
                className={`font-medium underline ${isDark ? 'text-purple-300 hover:text-purple-200' : 'text-purple-700 hover:text-purple-800'}`}
              >
                plan details
              </Link>{' '}
              for more information.
            </TipBox>

            {/* Integrations */}
            <h2 id="integrations" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Integrations
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Access Integrations by clicking the <strong>plug icon</strong> in the left panel. Connect SweetPilot with your favorite tools and services.
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Connect social media accounts (Facebook, Instagram, LinkedIn, etc.)</li>
              <li>Link project management tools (Trello, Asana, etc.)</li>
              <li>Integrate communication platforms (Slack, Discord, etc.)</li>
              <li>Connect cloud storage services (Google Drive, Dropbox, etc.)</li>
            </ul>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Once connected, your AI employees can access and work with data from these platforms, making your workflows more efficient.
            </p>
            <Link
              to="/doc/collections/integrations"
              className={`inline-flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-colors ${
                isDark
                  ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              Learn about Integrations
            </Link>

            {/* API */}
            <h2 id="api" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              API
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              The API section is accessed by clicking the <strong>code brackets icon</strong>. This is where developers can access SweetPilot programmatically.
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Generate and manage API keys</li>
              <li>View API documentation</li>
              <li>Monitor API usage and rate limits</li>
              <li>Access code examples and SDKs</li>
            </ul>
            <Link
              to="/doc/collections/api-reference"
              className={`inline-flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-colors ${
                isDark
                  ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              View API Reference
            </Link>

            {/* Billing */}
            <h2 id="billing" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Billing
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Access Billing settings by clicking the <strong>credit card icon</strong> in the left panel. Here you'll manage everything related to your subscription and payments.
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>View your current subscription plan</li>
              <li>Upgrade or downgrade your plan</li>
              <li>Add or update payment methods</li>
              <li>View billing history and invoices</li>
              <li>Purchase additional credits</li>
              <li>Apply promo codes or gift cards</li>
            </ul>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              You can also see your current credit balance at the bottom of the sidebar, showing your available credits and an option to add more.
            </p>

            {/* Summary */}
            <h2 id="summary" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Summary
            </h2>
            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              By familiarizing yourself with these key areas, you can navigate the SweetPilot web app efficiently and make the most of its features. We're excited to help you get started and make the most out of your experience with us!
            </p>

            {/* Divider */}
            <hr className={`my-12 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-2xl border overflow-hidden ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {article.title}
                  </span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>

            {/* Feedback Section */}
            <div className={`mt-12 p-8 rounded-2xl text-center ${
              isDark ? 'bg-dark-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
            }`}>
              <p className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="No">😞</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat">😐</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Yes">😃</button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Sidebar - Table of Contents */}
        <aside className="hidden lg:block w-56 shrink-0 ml-auto">
          <div className="sticky top-24 pl-6 border-l border-slate-200 dark:border-white/10">
            {/* Sidebar Title */}
            <h3 className={`text-base mb-4 ${
              isAtTop
                ? isDark ? 'text-white font-semibold' : 'text-slate-900 font-semibold'
                : isDark ? 'text-gray-400' : 'text-slate-600'
            }`}>
              Navigation bar in the web app
            </h3>
            <nav className="space-y-2">
              {tableOfContents.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-1.5 text-sm transition-colors ${
                    activeSection === item.id
                      ? isDark
                        ? 'text-white font-medium'
                        : 'text-slate-900 font-medium'
                      : isAtTop && index === 0
                        ? isDark
                          ? 'text-white font-medium'
                          : 'text-slate-900 font-medium'
                        : isDark
                          ? 'text-gray-500 hover:text-gray-300'
                          : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-16"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🧭</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
      </StaticPageLayout>
    </>
  );
}
